import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import Modal from '../../../shared/components/Modal';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import {
  createRatioDefinition,
  fetchRatioDefinitions,
  updateRatioDefinition
} from '../../../services/ratio';
import './RatioDefinitionsPage.css';

const API_STATUS_OPTIONS = ['Aktif', 'Pasif'];

const UI_TEXT = {
  'tr-TR': {
    pageTitle: 'Sistem Rasyolari',
    pageDescription: 'Sistem yonetimi icin rasyo tanimlari.',
    newRatio: 'Yeni Rasyo',
    code: 'Kod',
    name: 'Isim',
    category: 'Kategori',
    formula: 'Formul',
    status: 'Durum',
    action: 'Aksiyon',
    loading: 'Yukleniyor...',
    noRecords: 'Henuz rasyo tanimi bulunamadi.',
    edit: 'Duzenle',
    deactivate: 'Pasif Yap',
    activate: 'Aktive Et',
    close: 'Kapat',
    save: 'Kaydet',
    update: 'Guncelle',
    cancel: 'Iptal',
    saving: 'Kaydediliyor...',
    active: 'Aktif',
    passive: 'Pasif',
    editAria: 'Tanimi duzenle',
    createAria: 'Yeni rasyo tanimi',
    editTitle: 'Tanimi duzenle',
    createTitle: 'Yeni rasyo tanimi',
    numerator: 'Sayac',
    denominator: 'Payda',
    unit: 'Birim',
    decimals: 'Ondalik',
    higherIsBetter: 'Yuksek Iyi mi?',
    yes: 'Evet',
    no: 'Hayir',
    notes: 'Notlar',
    requiredError: 'Kod, isim ve formul zorunludur.',
    listLoadError: 'Tanimlar yuklenemedi.',
    operationFailed: 'Islem basarisiz oldu.',
    operationError: 'Islem sirasinda hata olustu.',
    updated: 'Rasyo tanimi guncellendi.',
    created: 'Yeni rasyo tanimi olusturuldu.',
    statusUpdated: 'Durum guncellendi.',
    statusUpdateError: 'Durum guncellenemedi.',
    notesPlaceholder: 'Cari oran aciklamasi...'
  },
  'en-US': {
    pageTitle: 'System Ratios',
    pageDescription: 'Ratio definitions for system management.',
    newRatio: 'New Ratio',
    code: 'Code',
    name: 'Name',
    category: 'Category',
    formula: 'Formula',
    status: 'Status',
    action: 'Action',
    loading: 'Loading...',
    noRecords: 'No ratio definition found yet.',
    edit: 'Edit',
    deactivate: 'Deactivate',
    activate: 'Activate',
    close: 'Close',
    save: 'Save',
    update: 'Update',
    cancel: 'Cancel',
    saving: 'Saving...',
    active: 'Active',
    passive: 'Passive',
    editAria: 'Edit definition',
    createAria: 'New ratio definition',
    editTitle: 'Edit definition',
    createTitle: 'New ratio definition',
    numerator: 'Numerator',
    denominator: 'Denominator',
    unit: 'Unit',
    decimals: 'Decimals',
    higherIsBetter: 'Higher is Better?',
    yes: 'Yes',
    no: 'No',
    notes: 'Notes',
    requiredError: 'Code, name, and formula are required.',
    listLoadError: 'Definitions could not be loaded.',
    operationFailed: 'Operation failed.',
    operationError: 'An error occurred during operation.',
    updated: 'Ratio definition updated.',
    created: 'New ratio definition created.',
    statusUpdated: 'Status updated.',
    statusUpdateError: 'Status could not be updated.',
    notesPlaceholder: 'Current ratio description...'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

const createEmptyForm = () => ({
  id: null,
  ratioCode: '',
  name: '',
  category: '',
  formula: '',
  numeratorFields: '',
  denominatorFields: '',
  unit: '',
  decimals: 2,
  higherIsBetter: true,
  notes: '',
  status: 'Aktif'
});

const toListString = (values = []) => values.join(', ');

const parseListInput = (input) =>
  input
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

function RatioDefinitionsPage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formState, setFormState] = useState(createEmptyForm());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [listError, setListError] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');

  const isEditMode = Boolean(formState.id);
  const statusLabel = useCallback((value) => (value === 'Aktif' ? t('active') : t('passive')), [t]);

  const loadDefinitions = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const data = await fetchRatioDefinitions();
      setDefinitions(data);
    } catch (caught) {
      setListError(caught instanceof Error ? caught.message : t('listLoadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadDefinitions();
  }, [loadDefinitions]);

  const resetForm = useCallback(() => {
    setFormState(createEmptyForm());
    setFormError('');
  }, []);

  const openCreateModal = () => {
    resetForm();
    setSuccess('');
    setIsModalOpen(true);
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    resetForm();
  }, [resetForm]);

  const preparePayload = useMemo(
    () => ({
      ratioCode: formState.ratioCode.trim(),
      name: formState.name.trim(),
      category: formState.category.trim(),
      formula: formState.formula.trim(),
      numeratorFields: parseListInput(formState.numeratorFields),
      denominatorFields: parseListInput(formState.denominatorFields),
      unit: formState.unit.trim(),
      decimals: Number(formState.decimals),
      higherIsBetter: Boolean(formState.higherIsBetter),
      notes: formState.notes.trim(),
      status: formState.status
    }),
    [formState]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError('');
    setSuccess('');

    const payload = preparePayload;
    if (!payload.ratioCode || !payload.name || !payload.formula) {
      setFormError(t('requiredError'));
      setSaving(false);
      return;
    }

    try {
      if (isEditMode) {
        await updateRatioDefinition(formState.id, payload);
        setSuccess(t('updated'));
      } else {
        await createRatioDefinition(payload);
        setSuccess(t('created'));
      }
      closeModal();
      await loadDefinitions();
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : t('operationFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (definition) => {
    setFormState({
      id: definition.id,
      ratioCode: definition.ratioCode || '',
      name: definition.name || '',
      category: definition.category || '',
      formula: definition.formula || '',
      numeratorFields: toListString(definition.numeratorFields),
      denominatorFields: toListString(definition.denominatorFields),
      unit: definition.unit || '',
      decimals: definition.decimals ?? 0,
      higherIsBetter: Boolean(definition.higherIsBetter),
      notes: definition.notes || '',
      status: definition.status || 'Aktif'
    });
    setFormError('');
    setSuccess('');
    setIsModalOpen(true);
  };

  const handleToggleStatus = async (definition) => {
    setSaving(true);
    setListError('');
    setFormError('');
    setSuccess('');
    try {
      const payload = {
        ...definition,
        numeratorFields: definition.numeratorFields,
        denominatorFields: definition.denominatorFields,
        status: definition.status === 'Aktif' ? 'Pasif' : 'Aktif'
      };
      await updateRatioDefinition(definition.id, payload);
      setSuccess(t('statusUpdated'));
      if (formState.id === definition.id) closeModal();
      await loadDefinitions();
    } catch (caught) {
      setListError(caught instanceof Error ? caught.message : t('statusUpdateError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card ratio-definitions-card">
      <PageHeader
        title={t('pageTitle')}
        description={t('pageDescription')}
        actions={
          <button type="button" className="btn-secondary" onClick={openCreateModal}>
            {t('newRatio')}
          </button>
        }
      />
      {success && <div className="alert alert-success">{success}</div>}

      <div className="ratio-definitions-grid">
        <section className="ratio-definitions-table-panel">
          <div className="table-wrapper">
            <table className="ratio-definitions-table">
              <thead>
                <tr>
                  <th>{t('code')}</th>
                  <th>{t('name')}</th>
                  <th>{t('category')}</th>
                  <th>{t('formula')}</th>
                  <th>{t('status')}</th>
                  <th>{t('action')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="table-placeholder">{t('loading')}</td>
                  </tr>
                ) : listError ? (
                  <tr>
                    <td colSpan={6} className="table-placeholder">{listError}</td>
                  </tr>
                ) : definitions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="table-placeholder">{t('noRecords')}</td>
                  </tr>
                ) : (
                  definitions.map((definition) => (
                    <tr key={definition.id ?? definition.ratioCode}>
                      <td>{definition.ratioCode}</td>
                      <td>{definition.name}</td>
                      <td>{definition.category}</td>
                      <td>
                        <span className="formula">{definition.formula}</span>
                      </td>
                      <td>
                        <span className={`badge ${definition.status === 'Aktif' ? 'badge-active' : ''}`}>{statusLabel(definition.status)}</span>
                      </td>
                      <td className="action-cell">
                        <button
                          type="button"
                          className="action-icon-btn action-icon-btn-edit"
                          onClick={() => handleEdit(definition)}
                          aria-label={t('edit')}
                          title={t('edit')}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M3 17.25V21h3.75L18.81 8.94l-3.75-3.75L3 17.25Zm17.71-10.04a1.003 1.003 0 0 0 0-1.42L18.2 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 2.01-1.66Z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className={`status-toggle-btn ${definition.status === 'Aktif' ? 'status-toggle-btn-deactivate' : 'status-toggle-btn-activate'}`}
                          onClick={() => handleToggleStatus(definition)}
                          title={definition.status === 'Aktif' ? t('deactivate') : t('activate')}
                          aria-label={definition.status === 'Aktif' ? t('deactivate') : t('activate')}
                        >
                          {definition.status === 'Aktif' ? 'P' : 'A'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal} title={isEditMode ? t('editTitle') : t('createTitle')} width="560px">
        <section>
          {formError && <div className="alert alert-error">{formError}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-field">
              <label htmlFor="ratioCode">{t('code')}</label>
              <input
                id="ratioCode"
                name="ratioCode"
                value={formState.ratioCode}
                onChange={(event) => setFormState((current) => ({ ...current, ratioCode: event.target.value }))}
                placeholder="CURR"
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="name">{t('name')}</label>
              <input
                id="name"
                name="name"
                value={formState.name}
                onChange={(event) => setFormState((current) => ({ ...current, name: event.target.value }))}
                placeholder="Current Ratio"
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="category">{t('category')}</label>
              <input
                id="category"
                name="category"
                value={formState.category}
                onChange={(event) => setFormState((current) => ({ ...current, category: event.target.value }))}
                placeholder="liquidity"
              />
            </div>
            <div className="form-field">
              <label htmlFor="formula">{t('formula')}</label>
              <input
                id="formula"
                name="formula"
                value={formState.formula}
                onChange={(event) => setFormState((current) => ({ ...current, formula: event.target.value }))}
                placeholder="bs.current_assets / bs.short_term_liabilities"
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="numeratorFields">{t('numerator')}</label>
              <input
                id="numeratorFields"
                name="numeratorFields"
                value={formState.numeratorFields}
                onChange={(event) => setFormState((current) => ({ ...current, numeratorFields: event.target.value }))}
                placeholder="bs.current_assets, bs.inventories"
              />
            </div>
            <div className="form-field">
              <label htmlFor="denominatorFields">{t('denominator')}</label>
              <input
                id="denominatorFields"
                name="denominatorFields"
                value={formState.denominatorFields}
                onChange={(event) => setFormState((current) => ({ ...current, denominatorFields: event.target.value }))}
                placeholder="bs.short_term_liabilities"
              />
            </div>
            <div className="form-field">
              <label htmlFor="unit">{t('unit')}</label>
              <input
                id="unit"
                name="unit"
                value={formState.unit}
                onChange={(event) => setFormState((current) => ({ ...current, unit: event.target.value }))}
                placeholder="x"
              />
            </div>
            <div className="form-field">
              <label htmlFor="decimals">{t('decimals')}</label>
              <input
                id="decimals"
                name="decimals"
                type="number"
                min="0"
                value={formState.decimals}
                onChange={(event) => setFormState((current) => ({ ...current, decimals: Number(event.target.value) }))}
              />
            </div>
            <div className="form-field">
              <label htmlFor="higherIsBetter">{t('higherIsBetter')}</label>
              <select
                id="higherIsBetter"
                name="higherIsBetter"
                value={formState.higherIsBetter ? 'true' : 'false'}
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    higherIsBetter: event.target.value === 'true'
                  }))
                }
              >
                <option value="true">{t('yes')}</option>
                <option value="false">{t('no')}</option>
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="notes">{t('notes')}</label>
              <textarea
                id="notes"
                name="notes"
                value={formState.notes}
                onChange={(event) => setFormState((current) => ({ ...current, notes: event.target.value }))}
                placeholder={t('notesPlaceholder')}
              />
            </div>
            <div className="form-field">
              <label htmlFor="status">{t('status')}</label>
              <select
                id="status"
                name="status"
                value={formState.status}
                onChange={(event) => setFormState((current) => ({ ...current, status: event.target.value }))}
              >
                {API_STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>{statusLabel(option)}</option>
                ))}
              </select>
            </div>
            <div className="form-footer">
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? t('saving') : isEditMode ? t('update') : t('save')}
              </button>
              <button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>
                {t('cancel')}
              </button>
            </div>
          </form>
        </section>
      </Modal>
    </div>
  );
}

export default RatioDefinitionsPage;
